// Routes
export const ROUTE_PATHS = {
  HOME: '/',
} as const;

// Types
export interface Amenity {
  id: string;
  name: string;
  address: string;
  category: AmenityCategory;
  lat: number;
  lng: number;
  description?: string;
}

export type AmenityCategory =
  | 'listing'
  | 'grocery'
  | 'home_improvement'
  | 'shopping'
  | 'park'
  | 'highway'
  | 'hospital'
  | 'school'
  | 'pharmacy'
  | 'restaurant';

// Default listing (overridden by URL params at runtime)
export const DEFAULT_LISTING: Amenity = {
  id: 'listing',
  name: 'Promenade on Providence',
  address: '10844 Providence Rd, Charlotte, NC 28277',
  category: 'listing',
  lat: 35.0560,
  lng: -80.7870,
  description: 'Your new home — off I-485 & Providence Rd',
};

// Static amenities (no listing — listing is dynamic)
export const STATIC_AMENITIES: Amenity[] = [
  {
    id: 'harris_teeter',
    name: 'Harris Teeter',
    address: '10616 Providence Rd, Charlotte, NC 28277',
    category: 'grocery',
    lat: 35.0576,
    lng: -80.7867,
    description: 'Full-service grocery, pharmacy & pickup',
  },
  {
    id: 'lowes',
    name: "Lowe's Home Improvement",
    address: '5310 Ballantyne Commons Pkwy, Charlotte, NC 28277',
    category: 'home_improvement',
    lat: 35.0598,
    lng: -80.7750,
    description: 'Home improvement supplies & appliances',
  },
  {
    id: 'home_depot',
    name: 'The Home Depot',
    address: '5415 Ballantyne Commons Pkwy, Charlotte, NC 28277',
    category: 'home_improvement',
    lat: 35.0622,
    lng: -80.7742,
    description: 'Tools, materials & home improvement',
  },
  {
    id: 'waverly',
    name: 'Waverly',
    address: 'Providence Rd & Ardrey Kell Rd, Charlotte, NC 28277',
    category: 'shopping',
    lat: 35.0480,
    lng: -80.7830,
    description: 'Mixed-use dining, retail & entertainment',
  },
  {
    id: 'arboretum',
    name: 'The Arboretum',
    address: '8008 Providence Rd, Charlotte, NC 28277',
    category: 'shopping',
    lat: 35.0960,
    lng: -80.7820,
    description: '80+ shops, restaurants & services',
  },
  {
    id: 'william_davie',
    name: 'William Davie Park',
    address: '4635 Pineville Matthews Rd, Charlotte, NC 28226',
    category: 'park',
    lat: 35.0760,
    lng: -80.7680,
    description: 'Mecklenburg County park with sports facilities',
  },
  {
    id: 'i485',
    name: 'I-485 Access',
    address: 'Charlotte Outer Belt — Providence Rd Exit',
    category: 'highway',
    lat: 35.0537,
    lng: -80.7870,
    description: 'Direct access to Charlotte Outer Belt',
  },
];

// Legacy export for backwards compat (used in PrintView)
export const AMENITIES: Amenity[] = [DEFAULT_LISTING, ...STATIC_AMENITIES];

// Category config
export interface CategoryConfig {
  label: string;
  color: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  emoji: string;
}

export const CATEGORY_CONFIG: Partial<Record<AmenityCategory, CategoryConfig>> & Record<'listing'|'grocery'|'home_improvement'|'shopping'|'park'|'highway', CategoryConfig> = {
  listing: {
    label: 'Your Listing',
    color: '#B8860B',
    bgClass: 'bg-primary',
    textClass: 'text-primary-foreground',
    borderClass: 'border-primary',
    emoji: '🏠',
  },
  grocery: {
    label: 'Grocery',
    color: '#4A7C59',
    bgClass: 'bg-green-700',
    textClass: 'text-white',
    borderClass: 'border-green-700',
    emoji: '🛒',
  },
  home_improvement: {
    label: 'Home Improvement',
    color: '#B85C38',
    bgClass: 'bg-orange-700',
    textClass: 'text-white',
    borderClass: 'border-orange-700',
    emoji: '🔨',
  },
  shopping: {
    label: 'Shopping Center',
    color: '#3B6FA0',
    bgClass: 'bg-blue-700',
    textClass: 'text-white',
    borderClass: 'border-blue-700',
    emoji: '🛍️',
  },
  park: {
    label: 'Parks & Recreation',
    color: '#2E7D32',
    bgClass: 'bg-green-800',
    textClass: 'text-white',
    borderClass: 'border-green-800',
    emoji: '🌳',
  },
  highway: {
    label: 'Highway Access',
    color: '#5A5A7A',
    bgClass: 'bg-slate-600',
    textClass: 'text-white',
    borderClass: 'border-slate-600',
    emoji: '🛣️',
  },
};

export const ACTIVE_CATEGORIES: AmenityCategory[] = [
  'listing',
  'grocery',
  'home_improvement',
  'shopping',
  'park',
  'highway',
  'hospital',
  'school',
  'pharmacy',
  'restaurant',
];

// ─── URL Params helpers ────────────────────────────────────────────────────
/** Build a shareable URL for a listing */
export function buildListingUrl(listing: Amenity, baseUrl?: string): string {
  const base = baseUrl ?? window.location.href.split('?')[0].split('#')[0];
  const params = new URLSearchParams({
    name: listing.name,
    address: listing.address,
    lat: String(listing.lat),
    lng: String(listing.lng),
  });
  return `${base}#/?${params.toString()}`;
}

/** Parse listing from URL search params, falling back to default */
export function parseListingFromParams(searchParams: URLSearchParams): Amenity | null {
  const lat = parseFloat(searchParams.get('lat') ?? '');
  const lng = parseFloat(searchParams.get('lng') ?? '');
  const name = searchParams.get('name');
  const address = searchParams.get('address');
  if (name && address && !isNaN(lat) && !isNaN(lng)) {
    return { id: 'listing', name, address, category: 'listing', lat, lng, description: 'Your listing' };
  }
  return null; // no params = empty state, user must enter address
}

// ─── Dynamic Nearby Amenities (Overpass API) ─────────────────────────────

export interface NearbyResult {
  id: string;
  name: string;
  address: string;
  category: string;
  lat: number;
  lng: number;
  distanceMiles?: number;
  description?: string;
}

// ─── Photon-based nearby amenities ────────────────────────────────────────
// Photon (photon.komoot.io) is a free, CORS-enabled geocoding API backed by
// OpenStreetMap. Unlike Nominatim, it handles concurrent requests fine and
// doesn't require an API key. We search by brand name + category terms.

interface PhotonFeatureInternal {
  type: string;
  geometry: { coordinates: [number, number] };
  properties: {
    name?: string;
    osm_key?: string;
    osm_value?: string;
    street?: string;
    housenumber?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

async function photonSearch(
  query: string,
  lat: number, lng: number,
  radiusDeg: number,
  limit = 8,
): Promise<PhotonFeatureInternal[]> {
  const bbox = `${lng - radiusDeg},${lat - radiusDeg},${lng + radiusDeg},${lat + radiusDeg}`;
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=${limit}&bbox=${encodeURIComponent(bbox)}`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(7000) });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.features ?? []).filter((f: PhotonFeatureInternal) => {
      const [fLng, fLat] = f.geometry.coordinates;
      return f.properties.name
        && Math.abs(fLat - lat) < radiusDeg * 2
        && Math.abs(fLng - lng) < radiusDeg * 2;
    });
  } catch { return []; }
}

function photonToResult(f: PhotonFeatureInternal, category: string, emoji: string, lat: number, lng: number): NearbyResult {
  const [fLng, fLat] = f.geometry.coordinates;
  const p = f.properties;
  const addr = [p.housenumber, p.street, p.city, p.state]
    .filter(Boolean).join(' ') || `${p.city ?? ''}, ${p.state ?? ''}`;
  const dist = haversineDistanceMiles(lat, lng, fLat, fLng);
  return {
    id: `${category}_${Math.round(fLat * 10000)}_${Math.round(fLng * 10000)}`,
    name: p.name!,
    address: addr.trim(),
    category,
    lat: fLat, lng: fLng,
    distanceMiles: dist,
    description: `${emoji} ${dist.toFixed(1)} mi away`,
  };
}

// Category definitions: each has named brand searches + a generic category term
const PLACE_CATEGORIES: Array<{
  category: string; emoji: string; radiusDeg: number; maxResults: number;
  queries: string[];
}> = [
  {
    category: 'grocery', emoji: '🛒', radiusDeg: 0.08, maxResults: 6,
    queries: ['Harris Teeter', 'Publix', 'Whole Foods', 'Trader Joes', 'Walmart', 'Aldi', 'Food Lion', 'Lidl', 'Kroger', 'Fresh Market', 'supermarket'],
  },
  {
    category: 'restaurant', emoji: '🍽️', radiusDeg: 0.05, maxResults: 8,
    queries: ['Chick-fil-A', 'Starbucks', 'Panera Bread', 'Chipotle', 'McDonalds', 'Bojangles', 'restaurant', 'cafe'],
  },
  {
    category: 'pharmacy', emoji: '💊', radiusDeg: 0.07, maxResults: 5,
    queries: ['CVS Pharmacy', 'Walgreens', 'Rite Aid', 'pharmacy'],
  },
  {
    category: 'school', emoji: '🏫', radiusDeg: 0.07, maxResults: 6,
    queries: ['school', 'elementary school', 'middle school', 'high school'],
  },
  {
    category: 'park', emoji: '🌳', radiusDeg: 0.08, maxResults: 5,
    queries: ['park', 'greenway', 'recreation center'],
  },
  {
    category: 'hospital', emoji: '🏥', radiusDeg: 0.12, maxResults: 5,
    queries: ['Atrium Health', 'Novant Health', 'urgent care', 'hospital', 'clinic'],
  },
  {
    category: 'shopping', emoji: '🛍️', radiusDeg: 0.09, maxResults: 6,
    queries: ['Target', 'shopping center', 'mall', 'department store'],
  },
  {
    category: 'home_improvement', emoji: '🔨', radiusDeg: 0.12, maxResults: 4,
    queries: ['Home Depot', "Lowe's", 'Ace Hardware', 'hardware store'],
  },
];

export async function fetchNearbyAmenities(lat: number, lng: number): Promise<NearbyResult[]> {
  const final: NearbyResult[] = [];

  for (const cat of PLACE_CATEGORIES) {
    const seen = new Set<string>();
    const items: NearbyResult[] = [];

    // Run queries for this category in parallel — Photon handles concurrent requests
    const results = await Promise.allSettled(
      cat.queries.map(q => photonSearch(q, lat, lng, cat.radiusDeg, 5))
    );

    for (const r of results) {
      if (r.status !== 'fulfilled') continue;
      for (const f of r.value) {
        const name = f.properties.name!;
        if (seen.has(name)) continue;
        seen.add(name);
        items.push(photonToResult(f, cat.category, cat.emoji, lat, lng));
      }
    }

    items.sort((a, b) => (a.distanceMiles ?? 0) - (b.distanceMiles ?? 0));
    final.push(...items.slice(0, cat.maxResults));

    // Small delay between categories to be respectful to Photon
    await new Promise(r => setTimeout(r, 100));
  }

  return final;
}

export const DYNAMIC_CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  hospital: { label: 'Hospital / Urgent Care', color: '#C62828', bgClass: 'bg-red-800', textClass: 'text-white', borderClass: 'border-red-800', emoji: '🏥' },
  school:   { label: 'School',                 color: '#6A1B9A', bgClass: 'bg-purple-800', textClass: 'text-white', borderClass: 'border-purple-800', emoji: '🏫' },
  pharmacy: { label: 'Pharmacy',               color: '#0277BD', bgClass: 'bg-blue-800',   textClass: 'text-white', borderClass: 'border-blue-800',   emoji: '💊' },
  restaurant:{ label: 'Restaurant',            color: '#E65100', bgClass: 'bg-orange-800', textClass: 'text-white', borderClass: 'border-orange-800', emoji: '🍽️' },
  grocery:  { label: 'Grocery',                color: '#2E7D32', bgClass: 'bg-green-800',  textClass: 'text-white', borderClass: 'border-green-800',  emoji: '🛒' },
  shopping: { label: 'Shopping Center',        color: '#1565C0', bgClass: 'bg-blue-900',   textClass: 'text-white', borderClass: 'border-blue-900',   emoji: '🛍️' },
  home_improvement: { label: 'Home Improvement', color: '#BF360C', bgClass: 'bg-red-900', textClass: 'text-white', borderClass: 'border-red-900',    emoji: '🔨' },
  park:     { label: 'Parks & Recreation',     color: '#1B5E20', bgClass: 'bg-green-900',  textClass: 'text-white', borderClass: 'border-green-900',  emoji: '🌳' },
};

export function getCategoryConfig(cat: string): CategoryConfig {
  if (cat in CATEGORY_CONFIG) return CATEGORY_CONFIG[cat as keyof typeof CATEGORY_CONFIG]!;
  if (cat in DYNAMIC_CATEGORY_CONFIG) return DYNAMIC_CATEGORY_CONFIG[cat];
  return { label: cat, color: '#666', bgClass: 'bg-gray-600', textClass: 'text-white', borderClass: 'border-gray-600', emoji: '📍' };
}

// ─── Nominatim nearby amenities ───────────────────────────────────────────


/** Haversine straight-line distance in miles */
export function haversineDistanceMiles(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Road distance estimate: straight-line × 1.35 multiplier (suburban factor) */
export function estimatedRoadMiles(straightLineMiles: number): number {
  return straightLineMiles * 1.35;
}

/** Geocode an address via Nominatim (free, no API key needed) */
export async function geocodeAddress(
  address: string,
): Promise<{ lat: number; lng: number } | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1&countrycodes=us`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en', 'User-Agent': 'SoldOnDanielle/1.0' },
    });
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
  } catch (_) { /* ignore */ }
  return null;
}

// ─── Showing Cost Calculation ─────────────────────────────────────────────
export const IRS_MILEAGE_RATE_2025 = 0.725; // $ per mile — IRS standard rate 2026

export interface ShowingCalcInput {
  oneWayMiles: number;      // road miles one way
  driveTimeOneWayMins: number; // estimated drive time one way
  timeAtPropertyMins: number;  // time spent at the property
  hourlyRate: number;          // agent's hourly labor rate
  mileageRate: number;         // $/mile
}

export interface ShowingCalcResult {
  roundTripMiles: number;
  totalTimeMins: number;
  mileageCost: number;
  laborCost: number;
  subtotal: number;
  roundedTotal: number;
}

export function calculateShowingCost(input: ShowingCalcInput): ShowingCalcResult {
  const roundTripMiles = input.oneWayMiles * 2;
  const totalTimeMins = input.driveTimeOneWayMins * 2 + input.timeAtPropertyMins;
  const totalHours = totalTimeMins / 60;
  const mileageCost = roundTripMiles * input.mileageRate;
  const laborCost = totalHours * input.hourlyRate;
  const subtotal = mileageCost + laborCost;
  // Round up to nearest $5
  const roundedTotal = Math.ceil(subtotal / 5) * 5;
  return { roundTripMiles, totalTimeMins, mileageCost, laborCost, subtotal, roundedTotal };
}
